/**
 * Cart drawer — pure-client progress bar reconciliation.
 *
 * Runs after cart-drawer-section-sync.js (qualifyingSpendCentsFromCart is
 * pulled from realm scope) and registers the authoritative cart:updated
 * normalizer listener that keeps the progress bar headline, fill, pins,
 * and labels in sync with the live cart total — regardless of whether
 * the Section Render API returned a correct/complete drawer HTML.
 */

/**
 * Pure-client reconciliation of the cart-drawer progress headline + fill.
 *
 * This is a defense-in-depth path that does NOT depend on the AJAX section
 * render being correct. It reads merchant config emitted as JSON by
 * snippets/cart-drawer-progress.liquid (`#CartDrawer-ProgressConfig`) and
 * recomputes the dynamic state — headline text, fill width, pin/label
 * completion, aria-valuenow — from whatever authoritative cart total the
 * caller supplies. Even if `mergeLiveProgressNode` adopts a blank headline
 * from a degraded server render, this listener overwrites it with the
 * correct computed text on the very next cart:updated tick.
 */
// Exposed on window so cart-drawer.js can self-heal the headline when the
// drawer is opened without a preceding cart:updated event.
function reconcileCartDrawerProgressHeadline(totalCents) {
  try {
    const configEl = document.getElementById('CartDrawer-ProgressConfig');
    if (!configEl) return;
    let config;
    try {
      config = JSON.parse(configEl.textContent || '{}');
    } catch (_) {
      return;
    }
    if (!config || !Array.isArray(config.tiers) || config.tiers.length === 0) return;

    const mc = config.mc | 0;
    const tiers = config.tiers;
    const finalIdx = Math.max(0, Math.min(tiers.length - 1, mc - 1));
    const finalTier = tiers[finalIdx];
    if (!finalTier || typeof finalTier.cents !== 'number') return;

    // Find the CLOSEST tier the customer has not yet reached. The headline
    // tracks THIS tier ("You are $X away from [next reward]") — as
    // thresholds are crossed it advances to the next one automatically.
    let nextIdx = -1;
    for (let i = 0; i < tiers.length; i++) {
      if (typeof tiers[i].cents === 'number' && totalCents < tiers[i].cents) {
        nextIdx = i;
        break;
      }
    }
    const nextTier = nextIdx >= 0 ? tiers[nextIdx] : null;

    const progressRoot = document.getElementById('CartDrawer-Progress');
    if (!progressRoot) return;
    const headlineEl = progressRoot.querySelector('.cart-drawer__progress-headline');
    const fillEl = progressRoot.querySelector('.cart-drawer__progress-fill');
    const trackEl = progressRoot.querySelector('.cart-drawer__progress-track');

    const allUnlocked = totalCents >= finalTier.cents;

    // 1) Headline text — NEVER leave the paragraph empty. The all-unlocked
    //    branch falls back to the translated "Every reward is yours" copy
    //    (config.unlocked_default) so a merchant who hasn't filled in a
    //    custom unlocked_text still gets a sentence. The working-toward
    //    branch always produces "You are $X away [from Y]". The final write
    //    is unconditional so stale empty content from a prior render is
    //    always healed on the next cart:updated tick.
    if (headlineEl) {
      let headlineText = '';
      if (allUnlocked || !nextTier) {
        const unlocked = (config.unlocked_text || '').trim();
        const unlockedDefault = (config.unlocked_default || '').trim();
        headlineText = unlocked || unlockedDefault || 'Every reward is yours';
      } else {
        const remaining = Math.max(0, nextTier.cents - totalCents);
        let fmtAmt;
        try {
          if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
            fmtAmt = window.Shopify.formatMoney(remaining, window.Shopify.money_format);
          } else {
            fmtAmt = '$' + (remaining / 100).toFixed(2);
          }
        } catch (_) {
          fmtAmt = '$' + (remaining / 100).toFixed(2);
        }
        // Label of the NEXT tier. If blank, walk FORWARD through later
        // tiers — never backward onto a reward the customer already
        // unlocked.
        let label = '';
        for (let i = nextIdx; i < tiers.length; i++) {
          const lbl = (tiers[i] && tiers[i].label) ? String(tiers[i].label).trim() : '';
          if (lbl) { label = lbl; break; }
        }
        const template = config.headline_template || '';
        if (label && template) {
          headlineText = template.replace('[[amount]]', fmtAmt).replace('[[reward]]', label);
        } else if (label) {
          headlineText = 'You are ' + fmtAmt + ' away from ' + label;
        } else {
          headlineText = 'You are ' + fmtAmt + ' away';
        }
      }
      headlineEl.textContent = headlineText;
    }

    // 2) Fill width + aria-valuenow (still scaled against the FINAL tier so
    //    the bar's total length doesn't change as tiers unlock — only the
    //    headline text advances).
    const pct = finalTier.cents > 0
      ? Math.min(100, (totalCents * 100) / finalTier.cents)
      : 0;
    if (fillEl) fillEl.style.width = pct + '%';
    if (trackEl) trackEl.setAttribute('aria-valuenow', String(Math.round(pct)));

    // 3) Pin + label-slot completion state
    const pins = progressRoot.querySelectorAll('.cart-drawer__pin');
    const slots = progressRoot.querySelectorAll('.cart-drawer__label-slot');
    tiers.forEach((tier, i) => {
      const complete = typeof tier.cents === 'number' && totalCents >= tier.cents;
      if (pins[i]) pins[i].classList.toggle('cart-drawer__pin--complete', complete);
      if (slots[i]) slots[i].classList.toggle('cart-drawer__label-slot--complete', complete);
    });

    // 4) Root all-complete flag
    progressRoot.classList.toggle('cart-drawer__progress--all-complete', allUnlocked);
  } catch (_) { /* swallow — defense in depth must never throw */ }
}

if (typeof window !== 'undefined') {
  window.reconcileCartDrawerProgressHeadline = reconcileCartDrawerProgressHeadline;
}

if (typeof document !== 'undefined') {
  // Guard against infinite loops when we re-dispatch a normalized event.
  let _cartUpdatedNormalizing = false;

  document.addEventListener('cart:updated', (e) => {
    try {
      const cd = e && e.detail && e.detail.cartData;
      let totalCents = null;
      let hasAuthoritativeTotal = false;
      if (cd) {
        const hasPrice =
          typeof cd.total_price === 'number' || typeof cd.items_subtotal_price === 'number';
        // Must have a line-item array: qualifyingSpendCentsFromCart returns 0
        // when `items` is missing, which used to fire with total_price set and
        // flash the bar to 0% before /cart.js normalized (e.g. auto-gift
        // section render + cart:updated race).
        const hasLineItems = Array.isArray(cd.items);
        const lineCount = hasLineItems ? cd.items.length : 0;
        const reportedCountRaw = cd.item_count;
        const reportedCount =
          typeof reportedCountRaw === 'number'
            ? reportedCountRaw
            : parseInt(String(reportedCountRaw ?? ''), 10);
        const reportedItems = Number.isFinite(reportedCount) ? reportedCount : lineCount;
        // Empty `items` but nonzero item_count → truncated cart payload; wait for /cart.js.
        const payloadLineItemsTruncated = hasLineItems && lineCount === 0 && reportedItems > 0;
        const payloadLineItemsComplete = hasLineItems && !payloadLineItemsTruncated;
        if (hasPrice && hasLineItems && payloadLineItemsComplete) {
          totalCents = qualifyingSpendCentsFromCart(cd);
          hasAuthoritativeTotal = true;
        }
      }

      if (hasAuthoritativeTotal) {
        window.__lastCartTotalCents = totalCents;
        reconcileCartDrawerProgressHeadline(totalCents);
        return;
      }

      // No authoritative total in this event (e.g. /cart/add.js for a bundle
      // returns only the ADDED line items, not the whole cart). Fetch the
      // authoritative cart and re-dispatch cart:updated with the full data
      // so every listener (progress reconcile, gift selector tier
      // eligibility, etc.) reconciles against the real total.
      if (_cartUpdatedNormalizing) return;
      if (typeof routes === 'undefined' || !routes.cart_url) return;
      _cartUpdatedNormalizing = true;
      fetch(`${routes.cart_url}.js`, { headers: { Accept: 'application/json' } })
        .then((r) => (r.ok ? r.json() : null))
        .then((cart) => {
          if (cart && typeof cart.total_price === 'number') {
            window.__lastCartTotalCents = qualifyingSpendCentsFromCart(cart);
            document.dispatchEvent(new CustomEvent('cart:updated', {
              detail: { source: 'cart:updated/normalized', cartData: cart },
            }));
          }
        })
        .catch((err) => { if (window.DEBUG_CART) console.warn('[cart] progress-reconcile: /cart.js normalize fetch failed', err); })
        .finally(() => { _cartUpdatedNormalizing = false; });
    } catch (_) {
      _cartUpdatedNormalizing = false;
    }
  });
}
